import React, { useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { STAGE_LABEL } from "@/constants/roles";
import { ChevronDown, ChevronRight, Search, X, SlidersHorizontal, Upload } from "lucide-react";

const DELIVERY = [{ v: "", label: "Any" }, { v: "pickup", label: "Pick-up" }, { v: "shipping", label: "Shipping" }];
const DATASET = [{ v: "orders", label: "Orders" }, { v: "archive", label: "Old sheet" }, { v: "both", label: "Both" }];
const COL_CLS = { Product: "text-emerald-700", Vehicle: "text-sky-700", Appearance: "text-rose-700", Destination: "text-violet-700", Order: "text-amber-700", Customer: "text-slate-600", Totals: "text-slate-500" };
// Auto-fitting columns: never overflow, wrap to fewer columns on zoom / narrow screens.
const GRID = "grid gap-4 [grid-template-columns:repeat(auto-fit,minmax(15rem,1fr))]";
const ROW = "flex w-full items-center gap-2 min-h-8 px-2.5 py-1 rounded-md border text-xs";
const LABEL = "shrink-0 w-[4.75rem] text-left leading-tight";
const bare = "h-6 min-w-0 flex-1 border-0 shadow-none px-1 text-xs md:text-xs font-mono focus-visible:ring-0";

/** One aligned form row: label left · chosen values (or "Any") right · chevron. Click → searchable checkbox list. */
function FieldPick({ label, options = [], value = [], onChange, testid, render }) {
  const [q, setQ] = useState("");
  const shown = useMemo(() => options.filter((o) => String(render ? render(o) : o).toLowerCase().includes(q.toLowerCase())), [options, q, render]);
  const toggle = (v) => onChange(value.includes(v) ? value.filter((x) => x !== v) : [...value, v]);
  const active = value.length > 0;
  const chosen = value.map((v) => (render ? render(v) : v));
  const text = chosen.slice(0, 2).join(", ") + (chosen.length > 2 ? ` +${chosen.length - 2}` : "");
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={`rpt-pick-${testid}`}
          title={active ? `${label}: ${chosen.join(", ")}` : label}
          className={`${ROW} transition-colors ${active ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 bg-white text-slate-700 hover:border-slate-400"}`}
        >
          <span className={`${LABEL} ${active ? "text-white/70" : "text-slate-500"}`}>{label}</span>
          <span className={`flex-1 min-w-0 text-left truncate font-mono ${active ? "font-semibold" : "text-slate-400"}`} data-testid={`rpt-pick-${testid}-chosen`}>{active ? text : "Any"}</span>
          {active
            ? <span role="button" tabIndex={-1} onClick={(e) => { e.stopPropagation(); onChange([]); }} className="shrink-0 rounded-full p-0.5 hover:bg-white/20" title="Clear" data-testid={`rpt-clear-${testid}`}><X size={11} /></span>
            : <ChevronDown size={12} className="opacity-60 shrink-0" />}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64 p-2" data-testid={`rpt-pop-${testid}`}>
        <div className="relative mb-2">
          <Search size={12} className="absolute left-2 top-2.5 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={`Search ${label.toLowerCase()}…`} className="h-8 pl-6 text-xs" data-testid={`rpt-search-${testid}`} />
        </div>
        <div className="max-h-56 overflow-y-auto space-y-0.5">
          {shown.length === 0 && <div className="text-xs text-slate-400 px-1 py-2">No values</div>}
          {shown.map((o) => (
            <label key={o} className="flex items-center gap-2 px-1 py-1 rounded hover:bg-slate-50 cursor-pointer text-xs">
              <input type="checkbox" checked={value.includes(o)} onChange={() => toggle(o)} data-testid={`rpt-opt-${testid}-${String(o).replace(/[^a-z0-9]/gi, "_").toLowerCase()}`} />
              <span className="font-mono">{render ? render(o) : o}</span>
            </label>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

/** Segmented toggle that wraps cleanly instead of overflowing. */
function Seg({ value, options, onChange, testid }) {
  const btn = (on) => `px-2 h-6 text-[11px] font-medium rounded border whitespace-nowrap ${on ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`;
  return (
    <div className="flex flex-wrap gap-1" data-testid={`rpt-tri-${testid}`}>
      {options.map((o) => <button key={String(o.v)} type="button" className={btn(value === o.v)} onClick={() => onChange(o.v)} data-testid={`rpt-tri-${testid}-${String(o.v)}`}>{o.label}</button>)}
    </div>
  );
}

function Column({ title, children, testid }) {
  return (
    <div className="space-y-1.5 min-w-0" data-testid={testid}>
      <div className={`text-[10px] font-mono uppercase tracking-[0.18em] ${COL_CLS[title] || "text-slate-500"}`}>{title}</div>
      {children}
    </div>
  );
}

/** Label + free control (Year · City · Customer · Delivery …) in the same row style. */
function FieldRow({ label, children, testid }) {
  return (
    <div className={`${ROW} border-slate-200 bg-white`} data-testid={testid}>
      <span className={`${LABEL} text-slate-500`}>{label}</span>
      <div className="flex-1 min-w-0 flex flex-wrap items-center gap-1">{children}</div>
    </div>
  );
}

function FoldButton({ open, onClick, icon: Icon, label, count, testid }) {
  return (
    <button type="button" onClick={onClick} className={`${ROW} transition-colors ${open || count ? "border-slate-400 bg-slate-50 text-slate-800" : "border-dashed border-slate-300 text-slate-500 hover:border-slate-400"}`} data-testid={testid} aria-expanded={open}>
      {open ? <ChevronDown size={12} className="shrink-0" /> : <ChevronRight size={12} className="shrink-0" />}
      <Icon size={12} className="shrink-0 opacity-70" />
      <span className="flex-1 text-left">{label}</span>
      {count > 0 && <span className="rounded-full bg-slate-900 text-white text-[10px] px-1.5 tabular-nums">{count}</span>}
    </button>
  );
}

/**
 * Organized filter panel.
 * Main: search · PO # · Data  |  Product (Cat # · Front · Rear) · Vehicle (Brand · Model · Year) · Totals (Group by · Advanced options · More options).
 * Advanced options: F-Clamp · R-Clamp · Trim · Appearance · Destination · Order · Customer.
 * More options (SA): save report · export Excel · import old worksheet / Drive link — rendered from `tools`.
 */
export function FiltersV2({ f, set, opts, run, advOpen, setAdvOpen, moreOpen, setMoreOpen, hasArchive, isSA, advancedCount, tools }) {
  const pick = (k, label, render) => <FieldPick key={k} label={label} testid={k} options={opts[k] || []} value={f[k]} onChange={(v) => set(k, v)} render={render} />;
  return (
    <div className="rounded-xl border border-slate-200 bg-white shadow-sm" data-testid="rpt-filters">
      {/* Search bar */}
      <div className="flex flex-wrap items-center gap-2 px-4 py-3 border-b border-slate-100" data-testid="rpt-text-row">
        <div className="relative flex-1 min-w-[14rem]">
          <Search size={14} className="absolute left-2.5 top-2.5 text-slate-400" />
          <Input placeholder="Exact phrase — PO #, VIN, code, colour, note…" value={f.text} onChange={(e) => set("text", e.target.value)} onKeyDown={(e) => e.key === "Enter" && run()} className="h-9 pl-8 text-sm" data-testid="rpt-text" title="Matches the exact phrase as whole words" />
        </div>
        <Input placeholder="PO #" value={f.po} onChange={(e) => set("po", e.target.value)} onKeyDown={(e) => e.key === "Enter" && run()} className="h-9 w-24 text-sm font-mono" data-testid="rpt-po" title="PO number (digits are enough — 5061 finds PO-5061)" />
        {(hasArchive || isSA) && (
          <div className="inline-flex items-center gap-1.5 text-xs text-slate-500">Data <Seg value={f.dataset || "both"} options={DATASET} onChange={(v) => set("dataset", v)} testid="dataset" /></div>
        )}
      </div>

      {/* Main columns */}
      <div className={`${GRID} px-4 pt-4 pb-3`}>
        <Column title="Product" testid="rpt-group-product">
          {pick("catalog_number", "Cat #")}
          {pick("front_cover_code", "Front code")}
          {pick("rear_cover_code", "Rear code")}
        </Column>
        <Column title="Vehicle" testid="rpt-group-vehicle">
          {pick("brand", "Brand")}
          {pick("model", "Model")}
          <FieldPick label="Year" testid="years" options={opts.years || []} value={f.years || []} onChange={(v) => set("years", v)} />
        </Column>
      </div>

      {/* One line: Group by · Advanced options · Upload options */}
      <div className="flex flex-wrap items-center gap-2 px-4 pb-4" data-testid="rpt-group-totals">
        <div className="flex-1 min-w-[14rem]">
          <FieldRow label="Group by">
            <select value={f.group_by} onChange={(e) => set("group_by", e.target.value)} className="h-6 w-full min-w-0 bg-transparent text-xs outline-none" data-testid="rpt-group-by">
              <option value="">— none —</option>
              {(opts.group_by || []).map((g) => <option key={g.key} value={g.key}>{g.label}</option>)}
            </select>
          </FieldRow>
        </div>
        <div className="flex-1 min-w-[14rem]">
          <FoldButton open={advOpen} onClick={() => setAdvOpen((v) => !v)} icon={SlidersHorizontal} label="Advanced options" count={advancedCount} testid="rpt-advanced-toggle" />
        </div>
        {isSA && (
          <div className="flex-1 min-w-[14rem]">
            <FoldButton open={moreOpen} onClick={() => setMoreOpen((v) => !v)} icon={Upload} label="Upload options" count={0} testid="rpt-more-toggle" />
          </div>
        )}
      </div>

      {advOpen && (
        <div className={`${GRID} px-4 py-4 border-t border-dashed border-slate-200 bg-slate-50/60`} data-testid="rpt-advanced">
          <Column title="Product" testid="rpt-group-product-more">
            {pick("f_clamp_code", "F-Clamp")}
            {pick("r_clamp_code", "R-Clamp")}
            {pick("trim", "Trim")}
          </Column>
          <Column title="Appearance" testid="rpt-group-appearance">
            {pick("color", "Color")}
            {pick("logo", "Logo")}
            {pick("spacer", "Spacer")}
            {pick("rim_size", "Rim size")}
          </Column>
          <Column title="Destination" testid="rpt-group-destination">
            {pick("country", "Country")}
            {pick("state", "State / Prov")}
            <FieldRow label="City">
              <Input placeholder="any" value={f.city} onChange={(e) => set("city", e.target.value)} onKeyDown={(e) => e.key === "Enter" && run()} className={bare} data-testid="rpt-city" />
            </FieldRow>
          </Column>
          <Column title="Order" testid="rpt-group-order">
            {pick("status", "Stage", (s) => STAGE_LABEL[s] || s)}
            {pick("source", "Source")}
            <FieldRow label="Delivery">
              <Seg value={f.delivery} options={DELIVERY} onChange={(v) => set("delivery", v)} testid="delivery" />
            </FieldRow>
          </Column>
          <Column title="Customer" testid="rpt-group-customer">
            <FieldRow label="Name / mail">
              <Input placeholder="any" value={f.customer} onChange={(e) => set("customer", e.target.value)} onKeyDown={(e) => e.key === "Enter" && run()} className={bare} data-testid="rpt-customer" />
            </FieldRow>
            <label className={`${ROW} border-slate-200 bg-white text-slate-700 cursor-pointer`}>
              <span className={`${LABEL} text-slate-500`}>Cancelled</span>
              <input type="checkbox" checked={f.include_cancelled} onChange={(e) => set("include_cancelled", e.target.checked)} data-testid="rpt-include-cancelled" />
              <span className="whitespace-nowrap">include</span>
            </label>
          </Column>
        </div>
      )}

      {isSA && moreOpen && (
        <div className="px-4 py-4 border-t border-dashed border-slate-200 bg-slate-50/60 rounded-b-xl" data-testid="rpt-more">
          {tools}
        </div>
      )}
    </div>
  );
}
